import { Zap } from "lucide-react";

const PLAN_LABEL = {
    investor_lite: "Investor Lite",
    investor_pro: "Investor Pro",
    investor_plus: "Investor Plus",
};

function formatRenewal(iso) {
    if (!iso) return "next renewal";
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            month: "short",
            day: "numeric",
            year: "numeric",
        });
    } catch {
        return "next renewal";
    }
}

export default function QuotaMeter({ quota }) {
    if (!quota) return null;
    const { plan, limit, used, remaining, period_end } = quota;
    const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
    const exhausted = remaining <= 0;
    // Color the bar based on consumption — calm teal → amber → rust
    const barColor = exhausted
        ? "bg-[#C85B3A]"  // rust
        : pct >= 80
          ? "bg-[#D4A62A]"  // amber
          : "bg-bl-primary"; // teal
    return (
        <div className="bl-card p-5 md:p-6" data-testid="quota-meter">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-lg bg-bl-teal-tint flex items-center justify-center">
                        <Zap className="w-4 h-4 text-bl-primary" />
                    </div>
                    <div>
                        <div className="text-[10px] tracking-[0.2em] uppercase text-bl-muted">
                            {PLAN_LABEL[plan] || plan}
                        </div>
                        <div className="font-display text-bl text-lg leading-tight">
                            Monthly reports
                        </div>
                    </div>
                </div>
                <div className="text-right">
                    <div
                        className="font-mono-bl text-2xl text-bl tnum leading-none"
                        data-testid="quota-meter-used"
                    >
                        {used}
                        <span className="text-bl-muted text-base"> / {limit}</span>
                    </div>
                    <div className="mt-1 text-xs text-bl-muted">
                        {remaining} remaining
                    </div>
                </div>
            </div>
            <div
                className="mt-4 w-full h-2 bg-bl-bg rounded-full overflow-hidden"
                role="progressbar"
                aria-valuenow={used}
                aria-valuemin={0}
                aria-valuemax={limit}
                aria-label={`${used} of ${limit} uploads used this billing period`}
            >
                <div
                    className={`h-full ${barColor} transition-all duration-700 ease-out`}
                    style={{ width: `${pct}%` }}
                />
            </div>
            <p className="mt-3 text-xs text-bl-muted leading-relaxed">
                {exhausted ? (
                    <span className="text-bl">
                        You've used all reports for this billing period.{" "}
                    </span>
                ) : null}
                Monthly usage is based on{" "}
                <span className="text-bl">generated</span> reports, not
                currently stored reports. Deleting a report doesn't free a
                slot. Resets on{" "}
                <span className="text-bl">{formatRenewal(period_end)}</span>.
            </p>
        </div>
    );
}
